import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// components
import Header from "../layout/Header";
import Input from "../components/Input";
import Button from "../components/Button";
import Alert from "../components/Alert";
import EmailVerificationModal from "../../auth/modals/EmailVerificationModal";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import ClientBranding from "../components/ClientBranding";

// servics
import { validateEmail, validateName } from "../../auth/utils/Validations";
import { getPreferences } from "../../video/services/videoService";
import { sendVerification } from "../../auth/services/authService";

const BasicInfo = () => {
  const navigate = useNavigate();
  const [employerPreferences, setEmployerPreferences] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    name: "",
  });
  const [showVerification, setShowVerification] = useState(false);
  const [showSendEmailErrorAlert, setShowSendEmailErrorAlert] = useState(false);
  const [showSendEmailSuccessAlert, setShowSendEmailSuccessAlert] =
    useState(false);
  const [emailAlertMessage, setEmailAlertMessage] = useState("");
  const [showVerifyCodeAlert, setShowVerifyCodeAlert] = useState(false);

  useEffect(() => {
    const fetchPreferences = async () => {
      try {
        const prefs = await getPreferences();
        setEmployerPreferences(prefs);
      } catch (error) {
        console.error("Error fetching preferences:", error);
      }
    };
    fetchPreferences();
  }, []);

  useEffect(() => {
    console.log("Employer preferences:", employerPreferences);
  }, [employerPreferences]);

  /* Handle email verification 
  - validate email
  - send verification email
  - show verification modal
  */
  const handleEmailVerification = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    // Validate email format
    if (!validateEmail(formData.email)) {
      setEmailAlertMessage("Please enter a valid email address.");
      setShowSendEmailErrorAlert(true);
      setIsLoading(false);
      return;
    }
    // Validate name format and show error alert if invalid
    if (!validateName(formData.name)) {
      setEmailAlertMessage("Please enter your first and last name.");
      setShowSendEmailErrorAlert(true);
      setIsLoading(false);
      return;
    }
    // Send verification email and show alert
    const result = await sendVerification(formData);
    if (result.success) {
      setEmailAlertMessage(result.data.message);
      setShowSendEmailSuccessAlert(true);
      setShowVerification(true);
      setIsLoading(false);
    } else {
      console.error("Send verification email error:", result.errors);
      if (result.errors.email) {
        setEmailAlertMessage(result.errors.email);
      } else if (result.errors.name) {
        setEmailAlertMessage(result.errors.name);
      } else {
        setEmailAlertMessage("Something went wrong. Please try again.");
      }
      setShowSendEmailErrorAlert(true);
      setIsLoading(false);
    }
  };

  /* Handle modal close 
  Parameters:
  - success: close modal, register user, anonymously authenticate, and redirect to next page
  - error: display error message and offer to resend verification email
  - cancel: close modal
  */
  const handleModalClose = async (result) => {
    if (result === "success") {
      sessionStorage.clear();
      setShowVerification(false);
      navigate("/question", { state: { employerPreferences } });
    } else if (result === "error") {
      setShowVerifyCodeAlert(true);
    } else {
      setShowVerification(false);
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Alert
        title="Error"
        message="The code you entered is incorrect. Please try again."
        showAlert={showVerifyCodeAlert}
        setShowAlert={setShowVerifyCodeAlert}
        colorClass="error"
      />
      <Alert
        title="Error"
        message={emailAlertMessage}
        showAlert={showSendEmailErrorAlert}
        setShowAlert={setShowSendEmailErrorAlert}
        colorClass="error"
      />
      <Alert
        title="Success"
        message={emailAlertMessage}
        showAlert={showSendEmailSuccessAlert}
        setShowAlert={setShowSendEmailSuccessAlert}
        colorClass="success"
      />
      <EmailVerificationModal
        isOpen={showVerification}
        onClose={handleModalClose}
        email={formData.email}
        name={formData.name}
      />
      <Header showLandingNav={false} />
      {isLoading ? (
        <div className="flex items-center justify-center min-h-screen">
          <ArrowPathIcon className="size-6 animate-spin" aria-label="loading" />
        </div>
      ) : (
        <div className="flex items-center justify-center flex-col min-h-[calc(100vh-4rem)]">
          <ClientBranding
            logoUrl={employerPreferences?.logoUrl}
            companyName={employerPreferences?.companyName}
          />
          <h1 className="text-2xl font-bold font-sans">
            Fill out some basic information before getting started:
          </h1>
          <form
            onSubmit={(e) => handleEmailVerification(e)}
            className="w-full flex flex-col items-center max-w-md p-6 space-y-6"
          >
            <Input
              type="text"
              placeholder="Full Name"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              label="Full Name"
              data-testid="name-input"
            />
            <Input
              type="email"
              placeholder="Email"
              value={formData.email}
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
              label="Email"
              data-testid="email-input"
            />
            <Button
              className="font-sans"
              type="submit"
              text="Submit"
              disabled={!formData.name || !formData.email}
              data-testid="submit-button"
            />
          </form>
        </div>
      )}
    </div>
  );
};

export default BasicInfo;
